import { useEffect, useState, ReactNode } from 'react';
import { FaSpinner } from 'react-icons/fa';
import { useSubscriptionButton } from '@/lib/payment/hooks';
import { UpgradeDialog } from './UpgradeDialog';
import { toast } from 'sonner';
import { useSubscription } from '@/contexts/SubscriptionContext';
// import { supabase } from '@/supabase/client'; // No longer needed for direct function call
import { PaymentService } from '@/supabase/payment/paymentService'; // Import PaymentService
import { logError } from '@/lib/sentry';
import { isUserCancelError } from '@/lib/payment/utils'; // For checking user cancellation
import { tiktokTrackSubscribe } from '@/lib/tiktok';

export interface SubscriptionButtonProps {
  userId: string;
  planId: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'subtle';
  size?: 'sm' | 'lg';
  fullWidth?: boolean;
  className?: string;
  disabled?: boolean;
  children?: ReactNode;
}

export function SubscriptionButton({
  userId,
  planId,
  size = 'lg',
  fullWidth = false,
  className = '',
  disabled = false,
  children
}: SubscriptionButtonProps) {
  const {
    isLoading: initialLoading, // Renamed to avoid conflict
    isPrepared,
    isSamePlan,
    isUpgrade,
    upgradeInfo,
    closeUpgradeDialog,
    executeUpgrade,
    handleSubscribe
  } = useSubscriptionButton(userId, planId);

  const {
    subscription,
    subscriptionPlan,
    refreshSubscription,
    setSubscriptionDataManually // 새 함수 가져오기
  } = useSubscription();
  const currentPlanId = subscriptionPlan?.id;
  const isPremiumTrial = subscription?.is_trial === true && currentPlanId === 'premium';

  const [isSubscribingFromTrial, setIsSubscribingFromTrial] = useState(false);
  const isLoading = initialLoading || isSubscribingFromTrial; // Combined loading state

  // 버튼 상태에 따른 라벨 관리
  const [buttonLabel, setButtonLabel] = useState('');
  useEffect(() => {
    if (isPremiumTrial) {
      if (planId === 'standard') {
        setButtonLabel('스탠다드 즉시 구독');
      } else if (planId === 'premium') {
        setButtonLabel('프리미엄 즉시 구독');
      } else {
        // 체험 중 다른 플랜 (예: 무료) 버튼은 기존 로직 따름
        setButtonLabel('구독하기');
      }
    } else if (isUpgrade) {
      setButtonLabel('업그레이드하기');
    } else if (isSamePlan) {
      setButtonLabel('현재 이용 중');
    } else {
      setButtonLabel('구독하기');
    }
  }, [isSamePlan, isUpgrade, isPremiumTrial, planId]);

  // 버튼 스타일 클래스들
  const baseClasses = 'relative inline-flex items-center justify-center rounded-lg text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50';
  
  const sizeClasses = size === 'sm' 
    ? 'h-9 px-3' 
    : 'h-11 px-8';

  const widthClass = fullWidth ? 'w-full' : '';
  
  // 플랜에 따른 커스텀 스타일
  const getPlanStyle = () => {
    // 프리미엄 체험 중 프리미엄 플랜 버튼은 '현재 이용 중' 스타일이 아님
    if (isSamePlan && !(isPremiumTrial && planId === 'premium')) {
      return 'bg-gray-200 text-gray-800 hover:bg-gray-300 font-medium';
    }

    if (planId === 'standard') {
      return 'bg-blue-600 hover:bg-blue-700 text-white';
    }
    
    if (planId === 'premium') {
      return 'bg-purple-600 hover:bg-purple-700 text-white';
    }
    
    return '';
  };
  
  // 버튼 클릭 핸들러
  const paymentService = new PaymentService(); // Instantiate PaymentService

  const handleSubscribeUsingPaymentService = async () => {
    if (!userId || !planId) return;

    setIsSubscribingFromTrial(true);
    const toastId = 'trial-to-paid-subscribe-toast';
    toast.loading('구독 처리 중입니다...', { id: toastId, duration: Infinity });

    try {
      // PaymentService.startSubscription handles UI for card registration if needed,
      // then payment, then activates subscription (expiring old ones).
      // 이제 Subscription | null을 반환합니다.
      const newSubscription = await paymentService.startSubscription(userId, planId);

      if (newSubscription) {
        // 새 구독 정보로 컨텍스트 수동 업데이트
        const newPlan = await paymentService.getSubscriptionPlan(newSubscription.subscription_plan_id);
        const newUsageStat = await paymentService.getCurrentUsageStat(userId, newSubscription.id);

        if (newPlan) {
          setSubscriptionDataManually(newSubscription, newPlan, newUsageStat);
          tiktokTrackSubscribe(newPlan); // 틱톡 구독 이벤트 전송
          toast.success('구독이 성공적으로 완료되었습니다!', { id: toastId, duration: 3000 });
          // 선택적으로 백그라운드에서 refreshSubscription을 호출하여 서버와 최종 동기화
          // refreshSubscription().catch(e => logError("Background refresh after manual update failed", e));
        } else {
          // 이 경우는 거의 발생하지 않아야 함 (planId가 유효하므로)
          logError('Failed to get plan details after new subscription', { newSubId: newSubscription.id, planId: newSubscription.subscription_plan_id });
          toast.error('구독 정보를 업데이트하는 데 실패했습니다. 페이지를 새로고침해주세요.', { id: toastId, duration: 5000 });
          await refreshSubscription(); // fallback to full refresh
        }
      } else {
        // newSubscription이 null이면 사용자가 결제를 취소했거나 startSubscription 내부에서 오류 발생
        // isUserCancelError는 startSubscription 내부에서 처리되거나 여기서 다시 확인할 수 있음
        toast.dismiss(toastId);
        // startSubscription에서 UserCancelError를 throw하지 않고 null을 반환하는 경우, 여기서 메시지 표시
        // if (err && !isUserCancelError(err)) { // startSubscription이 에러를 throw한 경우
        //  toast.info('구독 절차가 취소되었거나 오류가 발생했습니다.', { id: toastId, duration: 3000 });
        // }
      }
    } catch (err: any) {
      logError('Trial to Paid subscription via PaymentService failed', err, { userId, planId });
      if (isUserCancelError(err)) { // Check if it's a user cancellation error from startSubscription
        toast.info('구독 절차를 취소하셨습니다.', { id: toastId, duration: 3000 });
      } else {
        toast.error(`구독 처리 실패: ${err.message || '알 수 없는 오류가 발생했습니다.'}`, { id: toastId, duration: 5000 });
      }
    } finally {
      setIsSubscribingFromTrial(false);
    }
  };

  const handleButtonClick = () => {
    if (isPremiumTrial && (planId === 'standard' || planId === 'premium')) {
      handleSubscribeUsingPaymentService(); // Use the new handler
      return;
    }

    // 프리미엄 사용자가 (체험판 아님), 스탠다드 버튼을 누를 때 안내 메세지
    if (currentPlanId === 'premium' && !isPremiumTrial && planId === 'standard') {
      toast.info(
        '이미 프리미엄 플랜을 사용 중입니다.\n스탠다드로 변경하려면 \'구독 관리\'에서 다운그레이드를 예약해주세요.',
        {
          duration: 5000,
          position: 'top-center',
          style: {
            backgroundColor: '#EBEBFF',
            color: '#333',
            border: '1px solid #D4D4FF'
          },
        }
      );
      return;
    }

    // 그 외 경우는 기존 동작 유지
    handleSubscribe();
  };

  // Determine if the button should be disabled
  const isButtonDisabled = () => {
    // Universal disabling condition: if any loading is active.
    if (isLoading) {
      return true;
    }

    // Special handling for premium trial users wanting to subscribe to standard or premium.
    // This allows them to "subscribe" to the same plan ID if they are on a trial.
    if (isPremiumTrial && (planId === 'standard' || planId === 'premium')) {
      return false; // Enable for trial-to-paid conversion
    }

    // Standard disabling logic for all other cases.
    // `disabled` is the prop passed from MyPage (e.g., isCurrentPlan).
    // `isSamePlan` is from useSubscriptionButton hook.
    // We also add a specific check: if the user has a non-trial subscription to the current button's plan.
    if (
      !isPrepared ||
      disabled ||
      isSamePlan ||
      (subscription && !subscription.is_trial && currentPlanId === planId) // Explicitly disable if already on this non-trial plan
    ) {
      return true;
    }

    return false; // Enabled by default if no other condition met
  };


  return (
    <>
      <button
        onClick={handleButtonClick}
        disabled={isButtonDisabled()}
        className={`${baseClasses} ${sizeClasses} ${getPlanStyle()} ${widthClass} ${className}`}
      >
        {isLoading && (
          <FaSpinner className="mr-2 h-4 w-4 animate-spin" />
        )}
        {children || buttonLabel}
      </button>

      {/* 업그레이드 확인 다이얼로그 */}
      {upgradeInfo && (
        <UpgradeDialog
          open={upgradeInfo.showDialog}
          onOpenChange={closeUpgradeDialog}
          onConfirm={executeUpgrade}
          remainingDays={upgradeInfo.remainingDays}
          amount={upgradeInfo.amount}
          isLoading={isLoading} // Pass combined loading state
        />
      )}
    </>
  );
}